<template>
  <div class="mosaic-event-source-dialog" :aria-busy="isLoading || isSaving">
    <SpinnerLoading v-if="isLoading" color="blue" />
    <div v-else-if="hasLoadError" class="mosaic-event-source-dialog__message" role="alert">
      <p>{{ $t('loadMosaicEventSourceError') }}</p>
      <CamsButton @click="loadSources">
        {{ $t('retry') }}
      </CamsButton>
    </div>
    <template v-else>
      <div class="mosaic-event-source-dialog__source-actions">
        <SmartSelect
          v-if="mosaics.length"
          :value="isCreatingList ? null : selectedMosaicId"
          :options="mosaicOptions"
          :placeholder="$t('chooseEventSourceMosaic')"
          :disabled="isSaving"
          label-field="title"
          value-field="id"
          width="fill"
          @input="selectMosaic"
        >
          <template #option="{option}">
            {{ option.label }}
          </template>
        </SmartSelect>
        <CamsButton v-if="!isCreatingList" :disabled="isSaving" @click="startCreatingList">
          {{ $t('createCameraList') }}
        </CamsButton>
        <CamsButton v-else :disabled="isSaving" @click="cancelCreatingList">
          {{ $t('cancel') }}
        </CamsButton>
      </div>
      <p v-if="!mosaics.length && !isCreatingList">
        {{ $t('noEventSourceMosaics') }}
      </p>
      <SmartInputText
        v-if="isCreatingList"
        v-model="newListTitle"
        :caption="$t('cameraListName')"
        :placeholder="$t('enterTitle')"
        :disabled="isSaving"
        :make-focus="true"
        default-if-empty=""
        maxlength="255"
        @input="saveError = ''"
      />
      <div v-if="hasAircraftAnalytics" class="mosaic-event-source-dialog__event-filters">
        <SmartSwitch v-model="aircraftAudioEventsEnabled" :caption="$t('audioEvents')" />
        <SmartSwitch v-model="aircraftVideoEventsEnabled" :caption="$t('videoEvents')" />
      </div>
      <p v-if="saveError" class="mosaic-event-source-dialog__error" role="alert">
        {{ saveError }}
      </p>
      <SpinnerLoading v-if="isSaving" color="blue" />
      <MeshFrameDialog
        v-if="isCreatingList || selectedMosaic"
        :key="isCreatingList ? 'new-list' : selectedMosaic.id"
        :initial-selected-camera-numbers="isCreatingList ? [] : selectedMosaic.alarmCameraNumbers"
        :available-camera-numbers="availableCameraNumbers"
        :allow-select-all="true"
        :save-selection="saveSource"
      />
    </template>
  </div>
</template>

<script>
import MeshFrameDialog from "@/components/meshCameras/MeshFrameDialog.vue";
import {ACTION_LOAD_AIRCRAFT_ANALYTICS, ACTION_LOAD_AVAILABLE_ANALYTICS} from "@/store/analytics/index.js";
import {ANALYTICS} from "@/store/analytics/helpers.js";
import {aircraftEventFiltersMixin} from "@/components/events/aircraftEventFilters.js";
import {ACTION_LOAD_MOSAICS, ACTION_LOAD_MOSAIC, ACTION_SAVE_MOSAIC, GRIDS_MOSAICS} from "@/store/mosaics/index.js";
import {PAGE_SIZES} from "@/utils/consts.js";

export default {
  name: "MosaicEventSourceDialog",
  mixins: [aircraftEventFiltersMixin],
  components: {MeshFrameDialog},
  props: {
    initialMosaicId: {
      type: [String, Number],
      default: null,
    },
    onSourceSaved: {
      type: Function,
      default: null,
    },
    onSavingChange: {
      type: Function,
      default: null,
    },
    onEventFiltersChange: {
      type: Function,
      default: null,
    },
  },
  data() {
    return {
      mosaics: [],
      availableCameraNumbers: [],
      selectedMosaicId: this.initialMosaicId,
      isCreatingList: false,
      newListTitle: "",
      isLoading: false,
      hasLoadError: false,
      isSaving: false,
      hasAircraftAnalytics: false,
      saveError: "",
    };
  },
  computed: {
    mosaicOptions() {
      return this.mosaics.map(({id, title}) => ({id, title}));
    },
    selectedMosaic() {
      return this.mosaics.find(mosaic => String(mosaic.id) === String(this.selectedMosaicId)) || null;
    },
  },
  watch: {
    aircraftAudioEventsEnabled: "notifyEventFiltersChange",
    aircraftVideoEventsEnabled: "notifyEventFiltersChange",
  },
  mounted() {
    if (!this.initialMosaicId) {
      this.aircraftVideoEventsEnabled = false;
    }
    this.loadSources();
  },
  methods: {
    notifyEventFiltersChange() {
      if (this.onEventFiltersChange) {
        this.onEventFiltersChange({
          aircraftAudioEventsEnabled: this.aircraftAudioEventsEnabled,
          aircraftVideoEventsEnabled: this.aircraftVideoEventsEnabled,
        });
      }
    },
    async loadSources() {
      if (this.isLoading || this.isSaving || this._isDestroyed || this._isBeingDestroyed) {
        return;
      }
      this.isLoading = true;
      this.hasLoadError = false;
      try {
        const [mosaics, cameraNumbers, analytics] = await Promise.all([
          this.$store.dispatch(`mosaics/${ACTION_LOAD_MOSAICS}`, {}),
          this.loadAircraftCameraNumbers(),
          this.$store.dispatch(`analytics/${ACTION_LOAD_AVAILABLE_ANALYTICS}`),
        ]);
        if (this._isDestroyed || this._isBeingDestroyed) {
          return;
        }
        this.mosaics = mosaics;
        this.availableCameraNumbers = cameraNumbers;
        this.hasAircraftAnalytics = analytics.includes(ANALYTICS.aircraft);
        this.selectedMosaicId = this.selectedMosaic ? this.selectedMosaic.id : null;
      } catch {
        if (!this._isDestroyed && !this._isBeingDestroyed) {
          this.hasLoadError = true;
        }
      } finally {
        if (!this._isDestroyed && !this._isBeingDestroyed) {
          this.isLoading = false;
        }
      }
    },
    async loadAircraftCameraNumbers() {
      const cameraNumbers = [];
      for (let page = 1, pageCount = 1; page <= pageCount; page++) {
        if (this._isDestroyed || this._isBeingDestroyed) {
          return [];
        }
        const response = await this.$store.dispatch(`analytics/${ACTION_LOAD_AIRCRAFT_ANALYTICS}`, {
          page,
          pageSize: PAGE_SIZES.DEFAULT,
          orderBy: [],
          filters: [],
          search: "",
          fields: ["camera_number"],
        });
        if (this._isDestroyed || this._isBeingDestroyed) {
          return [];
        }
        cameraNumbers.push(...response.results.map(camera => camera.camera_number));
        pageCount = response.page.all;
      }
      return [...new Set(cameraNumbers)];
    },
    selectMosaic(mosaicId) {
      if (this.isSaving) {
        return;
      }
      this.selectedMosaicId = mosaicId;
      this.isCreatingList = false;
      this.saveError = "";
    },
    startCreatingList() {
      if (this.isSaving) {
        return;
      }
      this.isCreatingList = true;
      this.newListTitle = "";
      this.aircraftVideoEventsEnabled = false;
      this.saveError = "";
    },
    cancelCreatingList() {
      if (this.isSaving) {
        return;
      }
      this.isCreatingList = false;
      this.saveError = "";
    },
    async saveSource(alarmCameraNumbers) {
      if (this.isSaving || (!this.isCreatingList && !this.selectedMosaic) || this._isDestroyed || this._isBeingDestroyed) {
        return;
      }
      if (this.isCreatingList && !this.newListTitle.trim()) {
        this.saveError = this.$t('enterTitle');
        return;
      }
      this.isSaving = true;
      this.saveError = "";
      try {
        if (this.onSavingChange) {
          this.onSavingChange(true);
        }
        // Новый список хранится как мозаика с одной тревожной ячейкой.
        // Для существующего списка сохраняем актуальную сетку и настройки мозаики.
        const mosaic = this.isCreatingList ? {
          id: null,
          title: this.newListTitle.trim(),
          grid: GRIDS_MOSAICS.GRID1,
          viewCameraNumbers: [null],
          lowLatencyMode: true,
          alarmSlots: [0],
        } : await this.$store.dispatch(`mosaics/${ACTION_LOAD_MOSAIC}`, this.selectedMosaic.id);
        if (this._isDestroyed || this._isBeingDestroyed) {
          return;
        }
        if (!mosaic) {
          this.saveError = this.$t('mosaicEventSourceUnavailable');
          return;
        }
        const result = await this.$store.dispatch(`mosaics/${ACTION_SAVE_MOSAIC}`, {
          mosaicId: mosaic.id,
          mosaicTitle: mosaic.title,
          mosaicGrid: mosaic.grid,
          viewCameraNumbers: mosaic.viewCameraNumbers,
          lowLatencyMode: mosaic.lowLatencyMode,
          alarmCameraNumbers,
          alarmSlots: mosaic.alarmSlots,
        });
        mosaic.id = mosaic.id || (result && result.id);
        if (!mosaic.id) {
          throw new Error("Created camera list has no mosaic id");
        }
        mosaic.alarmCameraNumbers = [...alarmCameraNumbers];
        if (this.onSourceSaved) {
          this.onSourceSaved(mosaic);
        }
        if (!this._isDestroyed && !this._isBeingDestroyed) {
          this.$vuedals.close(mosaic);
        }
      } catch {
        if (!this._isDestroyed && !this._isBeingDestroyed) {
          this.saveError = this.$t('saveMosaicEventSourceError');
        }
      } finally {
        if (!this._isDestroyed && !this._isBeingDestroyed) {
          this.isSaving = false;
        }
        if (this.onSavingChange) {
          this.onSavingChange(false);
        }
      }
    },
  },
};
</script>

<style lang="scss">
.mosaic-event-source-dialog {
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: min(1515px, calc(100vw - 80px));
  height: calc(100vh - 160px);
  overflow: auto;
  padding-right: 10px;

  @media (max-width: 1660px) {
    width: min(1100px, calc(100vw - 80px));
  }

  @media (max-width: 1200px) {
    width: min(900px, calc(100vw - 80px));
  }

  .mesh-cameras_in-dialog {
    width: 100%;
    height: auto;
    overflow: visible;
    padding: 0;
  }

  &__event-filters {
    display: flex;
    flex-wrap: wrap;
    gap: 20px;
  }

  &__source-actions {
    display: flex;
    align-items: center;
    gap: 16px;

    > :first-child:not(:last-child) {
      flex: 1;
      min-width: 0;
    }

    > :last-child {
      flex-shrink: 0;
    }

    @media (max-width: 600px) {
      flex-wrap: wrap;

      > :first-child:not(:last-child) {
        flex-basis: 100%;
      }
    }
  }

  &__message {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  &__error {
    color: #c63333;
  }
}
</style>
